import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, X, Send, Phone } from "lucide-react";
import { COMPANY } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING =
    "Hi — I'm the Pacific Expressway assistant. Ask me about our services, location, hours, who to contact, quotes, or driving careers.";

const QUICK_CHIPS = [
    { label: "Where are you located?", testid: "chat-chip-location" },
    { label: "Who do I contact about billing?", testid: "chat-chip-billing" },
    { label: "How do I get a quote?", testid: "chat-chip-quote" },
    { label: "How do I apply to drive?", testid: "chat-chip-drive" },
];

function getSessionId() {
    let sid = localStorage.getItem("pe_chat_sid");
    if (!sid) {
        sid = crypto.randomUUID();
        localStorage.setItem("pe_chat_sid", sid);
    }
    return sid;
}

export default function ChatWidget() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const sessionId = useRef(getSessionId());
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open]);

    const send = async (text) => {
        const message = (text ?? input).trim();
        if (!message || busy) return;
        setInput("");
        setBusy(true);
        setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
        try {
            const resp = await fetch(`${API}/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId.current, message }),
            });
            if (!resp.ok || !resp.body) throw new Error("chat failed");
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop();
                for (const part of parts) {
                    if (!part.startsWith("data: ")) continue;
                    const data = part.slice(6);
                    if (data === "[DONE]") break;
                    try {
                        const { delta } = JSON.parse(data);
                        if (delta) {
                            setMessages((m) => {
                                const next = [...m];
                                next[next.length - 1] = {
                                    role: "assistant",
                                    content: next[next.length - 1].content + delta,
                                };
                                return next;
                            });
                        }
                    } catch {}
                }
            }
        } catch {
            setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = {
                    role: "assistant",
                    content: `I'm having trouble connecting right now — please call Dispatch at ${COMPANY.phone} and we'll help you right away.`,
                };
                return next;
            });
        } finally {
            setBusy(false);
        }
    };

    const showChips = messages.length === 1;

    return (
        <motion.div
            drag
            dragMomentum={false}
            dragElastic={0.08}
            className="fixed bottom-5 right-5 z-[70] sm:bottom-6 sm:right-6"
            style={{ touchAction: "none" }}
        >
            {open && (
                <div
                    data-testid="chat-panel"
                    role="dialog"
                    aria-label="Pacific Expressway assistant chat"
                    className="fixed inset-x-3 bottom-24 sm:inset-x-auto sm:absolute sm:bottom-full sm:right-0 sm:mb-4 flex h-[min(66vh,560px)] sm:w-[380px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl"
                    style={{ touchAction: "auto" }}
                >
                    <div className="flex items-center gap-3 bg-ink px-4 py-3.5">
                        <img src="/logo.png" alt="" className="h-7 w-auto rounded-sm bg-white px-1 py-0.5" />
                        <div className="min-w-0 flex-1">
                            <p className="font-heading text-sm font-bold tracking-wide text-white">
                                Pacific Assistant
                            </p>
                            <p className="text-xs text-gray-400">Answers about Pacific Expressway</p>
                        </div>
                        <a
                            href={COMPANY.phoneHref}
                            data-testid="chat-call-link"
                            aria-label={`Call ${COMPANY.phone}`}
                            className="inline-flex h-9 w-9 items-center justify-center text-gray-300 transition-colors hover:text-gold"
                        >
                            <Phone size={17} />
                        </a>
                        <button
                            type="button"
                            data-testid="chat-close-button"
                            onClick={() => setOpen(false)}
                            aria-label="Close chat"
                            className="inline-flex h-9 w-9 items-center justify-center text-gray-300 transition-colors hover:text-gold"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div
                        ref={scrollRef}
                        data-testid="chat-messages"
                        className="flex-1 space-y-3 overflow-y-auto bg-mist px-4 py-4"
                    >
                        {messages.map((msg, i) => (
                            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-[14px] leading-relaxed ${
                                        msg.role === "user"
                                            ? "bg-ink text-white"
                                            : "border border-gray-200 bg-white text-ink"
                                    }`}
                                >
                                    {msg.content ||
                                        (busy && i === messages.length - 1 ? (
                                            <span className="inline-flex gap-1 py-1" aria-label="Typing">
                                                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold" />
                                                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold [animation-delay:0.15s]" />
                                                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold [animation-delay:0.3s]" />
                                            </span>
                                        ) : null)}
                                </div>
                            </div>
                        ))}
                        {showChips && (
                            <div className="flex flex-wrap gap-2 pt-1">
                                {QUICK_CHIPS.map((chip) => (
                                    <button
                                        key={chip.testid}
                                        type="button"
                                        data-testid={chip.testid}
                                        onClick={() => send(chip.label)}
                                        className="rounded-full border border-gold/50 bg-white px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:bg-gold hover:text-ink"
                                    >
                                        {chip.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <form
                        className="flex items-center gap-2 border-t border-gray-200 bg-white p-3"
                        onSubmit={(e) => {
                            e.preventDefault();
                            send();
                        }}
                    >
                        <input
                            data-testid="chat-input"
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask about Pacific Expressway…"
                            aria-label="Chat message"
                            maxLength={1000}
                            className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2.5 text-[14px] focus:border-gold focus:outline-none"
                        />
                        <button
                            type="submit"
                            data-testid="chat-send-button"
                            disabled={busy || !input.trim()}
                            aria-label="Send message"
                            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gold text-ink transition-colors hover:bg-gold-light disabled:opacity-50"
                        >
                            <Send size={16} />
                        </button>
                    </form>
                </div>
            )}

            <motion.button
                type="button"
                data-testid="chat-widget-button"
                onTap={() => setOpen((v) => !v)}
                whileTap={{ scale: 0.92 }}
                aria-label={open ? "Close chat" : "Open chat with Pacific Assistant"}
                aria-expanded={open}
                className="flex h-14 w-14 cursor-grab items-center justify-center rounded-full bg-gold text-ink shadow-[0_8px_30px_rgba(0,0,0,0.35)] transition-colors hover:bg-gold-light active:cursor-grabbing"
            >
                {open ? <X size={24} /> : <MessageCircle size={24} />}
            </motion.button>
        </motion.div>
    );
}
